// The debrief header's SHARE and DOWNLOAD JSON chips (pitch 8, "shareable fights"). Split out of
// `src/ui/stages/DebriefStage.tsx` so that file's diff for this task is one import and one usage line —
// the rest of that header belongs to whoever is editing the debrief body this week.

import { useState } from 'react';
import type { Grid } from '../sim/grid';
import type { Dials, Scenario } from '../sim/types';
import type { PlanOrder } from '../state/store';
import type { EndedPayload } from '../state/debrief';
import { buildFightRecord, fightToUrl } from '../share/fight';

type State = 'idle' | 'busy' | 'copied' | 'fallback' | 'error';

const COPIED_MS = 2000;

export function ShareChip({
  grid, scenario, dials, plan, payload,
}: {
  grid: Grid; scenario: Scenario; dials: Dials; plan: PlanOrder[]; payload: EndedPayload | null;
}) {
  const [state, setState] = useState<State>('idle');
  const [url, setUrl] = useState<string | null>(null);

  async function share(): Promise<void> {
    if (!payload || state === 'busy') return;
    setState('busy');
    try {
      const rec = buildFightRecord({ grid, scenario, dials, plan, payload });
      const link = await fightToUrl(rec);
      setUrl(link);
      try {
        await navigator.clipboard.writeText(link);
        setState('copied');
        setTimeout(() => setState((s) => (s === 'copied' ? 'idle' : s)), COPIED_MS);
      } catch {
        // No clipboard permission (or no secure context): the link still built fine, so hand it over
        // as selectable text instead of failing the whole action.
        setState('fallback');
      }
    } catch {
      setState('error');
    }
  }

  function download(): void {
    if (!payload) return;
    const rec = buildFightRecord({ grid, scenario, dials, plan, payload });
    const blob = new Blob([JSON.stringify(rec)], { type: 'application/json' });
    const href = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = `windtunnel-fight-${scenario.seed}-${payload.snap.tick}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(href);
  }

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        data-testid="share-chip"
        onClick={() => { void share(); }}
        disabled={!payload || state === 'busy'}
        title="Copy a link that reproduces this fight exactly - area of operations, seed, dials, plan and every order, replayable from the URL"
        className="mono rounded-sm border border-slate-700 px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] text-paper disabled:opacity-40"
      >
        {state === 'copied' ? 'Copied' : state === 'busy' ? 'Building…' : 'Share'}
      </button>
      <button
        type="button"
        data-testid="download-json-chip"
        onClick={download}
        disabled={!payload}
        title="Download this fight as a JSON file - the same record the share link carries"
        className="mono rounded-sm border border-slate-700 px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] text-paper disabled:opacity-40"
      >
        Download JSON
      </button>
      {state === 'fallback' && url && (
        <span className="flex items-center gap-1.5">
          <input
            data-testid="share-url-fallback"
            readOnly
            value={url}
            onFocus={(e) => e.currentTarget.select()}
            className="mono w-[220px] rounded-sm border border-slate-700 bg-transparent px-2 py-1 text-[10px] text-paper"
          />
          <span className="mono text-[10px] text-fog">clipboard blocked — copy manually</span>
        </span>
      )}
      {state === 'error' && <span className="mono text-[10px] text-fog">could not build a share link</span>}
    </div>
  );
}
